<template>
  <picker-popup
    :column-count="3"
    :left-disabled="leftDisabled"
    :right-disabled="rightDisabled"
    :items="years"
    :zoom-out="pickerZoomOut"
    @left="previousPage"
    @right="nextPage"
    @element-click="$emit('select', $event)"
  >
    <template #heading>{{ heading }}</template>
  </picker-popup>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue'
import {
  startOfDecade,
  endOfDecade,
  eachYearOfInterval,
  getYear,
  subYears,
  addYears,
  isAfter,
  isBefore,
  getDecade,
  isValid,
} from 'date-fns'
import PickerPopup from '../datepicker/PickerPopup.vue'

export default defineComponent({
  components: {
    PickerPopup,
  },
  props: {
    selected: {
      type: [Date, Array] as PropType<Date | Date[] | null>,
      required: false,
      default: () => null,
    },
    pageDate: {
      type: Date as PropType<Date>,
      required: true,
    },
    lowerLimit: {
      type: Date as PropType<Date>,
      required: false,
      default: null,
    },
    upperLimit: {
      type: Date as PropType<Date>,
      required: false,
      default: null,
    },
    pickerZoomOut: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: {
    'update:pageDate': (date: Date) => isValid(date),
    select: (date: Date) => isValid(date),
  },
  setup(props, { emit }) {
    const from = computed(() => startOfDecade(props.pageDate))
    const to = computed(() => endOfDecade(props.pageDate))

    const isEnabled = (
      target: Date,
      lower: Date | undefined,
      upper: Date | undefined,
    ): boolean => {
      if (!lower && !upper) return true
      if (lower && getYear(target) < getYear(lower)) return false
      if (upper && getYear(target) > getYear(upper)) return false
      return true
    }

    const years = computed(() =>
      eachYearOfInterval({
        start: from.value,
        end: to.value,
      }).map((value: Date) => ({
        value,
        key: String(getYear(value)),
        display: getYear(value),
        selected: props.selected && getYear(value) === getYear(props.selected),
        disabled: !isEnabled(value, props.lowerLimit, props.upperLimit),
      })),
    )

    const heading = computed(() => {
      const start = getYear(from.value)
      const end = getYear(to.value)

      return `${start} - ${end}`
    })

    const leftDisabled = computed(
      () =>
        props.lowerLimit &&
        (getDecade(props.lowerLimit) === getDecade(props.pageDate) ||
          isBefore(props.pageDate, props.lowerLimit)),
    )
    const rightDisabled = computed(
      () =>
        props.upperLimit &&
        (getDecade(props.upperLimit) === getDecade(props.pageDate) ||
          isAfter(props.pageDate, props.upperLimit)),
    )

    const previousPage = () =>
      emit('update:pageDate', subYears(props.pageDate, 10))
    const nextPage = () => emit('update:pageDate', addYears(props.pageDate, 10))

    return {
      years,
      heading,
      leftDisabled,
      rightDisabled,
      previousPage,
      nextPage,
    }
  },
})
</script>
